"use client";

/**
 * P11-10 · First-party cookie-consent banner.
 *
 * Until this existed the `vx_consent` cookie was declared and read but never
 * written, which had two live consequences: first-party behavioural capture ran
 * on every visitor without being asked, and GA4 Consent Mode — which defaults
 * every signal to denied and waits to be granted — was never granted, so P9-05
 * collected nothing in production at all.
 *
 * Presentation notes:
 *  - Fixed to the bottom and rendered only after mount. Reading `document.cookie`
 *    during render would hydrate-mismatch, and reserving layout space for a bar
 *    most visitors never see would be worse than the overlay.
 *  - `role="dialog"` + `aria-modal` with a Tab trap. A consent prompt genuinely
 *    is a decision point, and letting Tab wander into a page whose analytics
 *    state is still undecided is exactly what the trap is for. Esc is
 *    deliberately NOT a dismissal — there is no "no answer" outcome.
 *  - Transitions are skipped under `prefers-reduced-motion`.
 */
import * as React from "react";

import { useTranslator } from "~/i18n/provider";
import {
  currentGrants,
  hasStoredConsent,
  writeConsent,
  type ConsentGrants,
} from "~/lib/analytics/consent";

const ALL: ConsentGrants = { analytics: true, marketing: true };
const NONE: ConsentGrants = { analytics: false, marketing: false };

/** Broadcast so the footer link can re-open the banner from anywhere. */
const REOPEN_EVENT = "vx:consent:reopen";

export function openConsentSettings(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
  }
}

export function ConsentBanner() {
  const t = useTranslator();
  const [visible, setVisible] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [grants, setGrants] = React.useState<ConsentGrants>(NONE);
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Mount-time only: the cookie is a browser fact, so the server render must
  // not depend on it.
  React.useEffect(() => {
    if (!hasStoredConsent()) setVisible(true);
  }, []);

  React.useEffect(() => {
    const reopen = () => {
      setGrants(currentGrants());
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener(REOPEN_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_EVENT, reopen);
  }, []);

  // Focus the panel on open, and keep Tab inside it while a choice is pending.
  React.useEffect(() => {
    if (!visible) return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = () =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled"));

    (focusables()[0] ?? panel).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    panel.addEventListener("keydown", onKeyDown);
    return () => panel.removeEventListener("keydown", onKeyDown);
  }, [visible, expanded]);

  function choose(next: ConsentGrants) {
    writeConsent(next);
    setVisible(false);
    setExpanded(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 motion-safe:animate-in motion-safe:slide-in-from-bottom-4"
      // The overlay is presentational; the panel below carries the semantics.
      data-testid="consent-banner"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-title"
        aria-describedby="consent-body"
        tabIndex={-1}
        className="w-full max-w-3xl rounded-xl border border-border bg-surface p-4 shadow-lg outline-none sm:p-5"
      >
        <h2 id="consent-title" className="text-sm font-semibold text-fg">
          {t("consent.title")}
        </h2>
        <p
          id="consent-body"
          className="mt-1 text-xs leading-relaxed text-fg-muted"
        >
          {t("consent.body")}
        </p>

        {expanded ? (
          <div className="mt-3 grid gap-2.5 border-t border-border pt-3">
            <ConsentRow
              title={t("consent.necessaryTitle")}
              body={t("consent.necessaryBody")}
              // Not a disabled checkbox: a control you can focus but never
              // change reads as broken. State it in words instead.
              control={
                <span className="text-[11px] font-semibold text-fg-muted">
                  {t("consent.necessaryAlways")}
                </span>
              }
            />
            <ConsentRow
              title={t("consent.analyticsTitle")}
              body={t("consent.analyticsBody")}
              control={
                <ConsentToggle
                  label={t("consent.analyticsTitle")}
                  checked={grants.analytics}
                  onChange={(analytics) =>
                    setGrants((g) => ({ ...g, analytics }))
                  }
                />
              }
            />
            <ConsentRow
              title={t("consent.marketingTitle")}
              body={t("consent.marketingBody")}
              control={
                <ConsentToggle
                  label={t("consent.marketingTitle")}
                  checked={grants.marketing}
                  onChange={(marketing) =>
                    setGrants((g) => ({ ...g, marketing }))
                  }
                />
              }
            />
          </div>
        ) : null}

        <div className="mt-3.5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => choose(ALL)}
            className="rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-fg hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {t("consent.acceptAll")}
          </button>
          {/*
            Reject is a peer of Accept, not a de-emphasised link: burying it is
            the dark pattern regulators actually enforce against.
          */}
          <button
            type="button"
            onClick={() => choose(NONE)}
            className="rounded-md border border-border px-4 py-2 text-xs font-semibold text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {t("consent.rejectAll")}
          </button>
          {expanded ? (
            <button
              type="button"
              onClick={() => choose(grants)}
              className="rounded-md border border-border px-4 py-2 text-xs font-semibold text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              {t("consent.save")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="rounded-md px-4 py-2 text-xs font-semibold text-fg-muted underline hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              {t("consent.customise")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConsentRow({
  title,
  body,
  control,
}: {
  title: string;
  body: string;
  control: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-fg">{title}</p>
        <p className="text-[11px] leading-relaxed text-fg-muted">{body}</p>
      </div>
      <div className="flex-none pt-0.5">{control}</div>
    </div>
  );
}

function ConsentToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  // A real checkbox rather than a styled div: it is keyboard-operable, exposes
  // its checked state to assistive tech, and needs no ARIA to do either.
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="size-4 accent-[var(--color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
    />
  );
}
